import type { VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { Link } from '@remix-run/react';
import { FC, ReactNode } from 'react';
import {
  cvaButton,
  cvaButtonInner,
  cvaButtonInnerRow,
  cvaIcon,
} from './ButtonStyles';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

interface Props
  extends VariantProps<typeof cvaButton>,
    VariantProps<typeof cvaButtonInnerRow> {
  href?: string;
  children?: ReactNode;
  onClick?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  type?: 'button' | 'submit';
  onlyIcon?: boolean;
  className?: string;
  innerRef?: any;
  dataCursor?: string;
  target?: string;
  isLoading?: boolean;
  hoverTextAnimation?: boolean;
  customIcon?: () => ReactNode;
}

export const Button: FC<Props> = ({
  href = '',
  innerRef,
  children,
  onClick,
  icon,
  disabled,
  type,
  onlyIcon,
  className,
  variant,
  justify = 'center',
  size,
  rubber,
  dataCursor,
  isLoading,
  target,
  customIcon,
}) => {
  return href ? (
    <Link
      target={target}
      rel={'noreferrer nofollower'}
      ref={innerRef}
      to={href}
      onClick={() => onClick?.()}
      className={clsx(
        cvaButton({
          isDisabled: disabled,
          variant,
          size,
          justify,
          rubber,
        }),
        className
      )}
      data-cursor={dataCursor}>
      {onlyIcon ? (
        <>
          {icon ? <div className={cvaIcon()}>{icon}</div> : null}{' '}
          {customIcon ? customIcon() : null} {children}
        </>
      ) : (
        <>
          {icon || customIcon ? (
            <>
              {icon ? (
                <div className={cvaIcon()}>{icon}</div>
              ) : customIcon ? (
                customIcon()
              ) : null}
            </>
          ) : null}
          <span className={cvaButtonInner()}>
            <span className={cvaButtonInnerRow({})}>
              {customIcon ? customIcon() : null} {children}
            </span>
          </span>
        </>
      )}
    </Link>
  ) : (
    <button
      ref={innerRef}
      disabled={disabled || isLoading}
      type={type ?? 'button'}
      onClick={() => onClick?.()}
      className={clsx(
        cvaButton({
          isDisabled: disabled && !isLoading,
          variant,
          size,
          justify,
          rubber,
        }),
        className
      )}
      data-cursor={dataCursor}>
      {onlyIcon ? (
        <>
          {icon ? (
            <div className={cvaIcon()}>{icon}</div>
          ) : customIcon ? (
            customIcon()
          ) : null}
        </>
      ) : isLoading ? (
        <LoadingSpinner
          size="button"
          className="absolute -translate-x-1/2 left-1/2 top-1/2 -translate-y-1/2 z-10"
        />
      ) : (
        <>
          {icon || customIcon ? (
            <>
              {icon ? <div className={cvaIcon()}>{icon}</div> : null}{' '}
              {customIcon ? customIcon() : null}
            </>
          ) : null}
          <span className={cvaButtonInner()}>
            <span className={cvaButtonInnerRow({})}>
              {customIcon ? customIcon() : null}
              {children}
            </span>
          </span>
        </>
      )}
    </button>
  );
};
